import { ITextLibrary } from '../interfaces'
import flatTexts from './flatTexts'

export enum AlertMessageType {
  outOfStock = 'outOfStock',
  AddItem = 'AddItem',
  RemoveItem = 'RemoveItem',
  GetCart = 'GetCart',
  Network = 'Network',
  ReorderPartial = 'ReorderPartial',
  ReorderFailed = 'ReorderFailed',
  SetPaymentMethod = 'SetPaymentMethod',
  SetDeliveryDate = 'SetDeliveryDate',
  CheckoutRuleRouteMissing = 'CheckoutRuleRouteMissing',
}

export type AlertMessageTypeKeys = keyof typeof AlertMessageType

export const getAlertErrorMessage = (
  errorType: AlertMessageType,
  texts?: ITextLibrary,
  extraData: number = 0,
): string => {
  const errors = flatTexts(texts?.errors)
  const orders = flatTexts(texts?.orders)
  const paymentMethod = flatTexts(texts?.paymentMethod)
  const deliveryDate = flatTexts(texts?.deliveryDate)
  const errorIsPlural = extraData > 1
  const stockError = errorIsPlural
    ? errors?.cartOutOfStockErrorPlural
    : errors?.cartOutOfStockErrorSingle
  switch (errorType) {
    case AlertMessageType.AddItem:
      return errors?.cartAddItemError || ''
    case AlertMessageType.RemoveItem:
      return errors?.cartRemoveItemError || ''
    case AlertMessageType.outOfStock:
      return stockError
        ? stockError.replace('{{stock}}', String(extraData))
        : ''
    case AlertMessageType.GetCart:
      return errors?.cartGetItemsError || ''
    case AlertMessageType.Network:
      return errors?.genericNetworkError || ''
    // Reorder succeeded, but the backend dropped lines that are no longer
    // purchasable. `extraData` is how many, substituted the same way
    // `outOfStock` substitutes `{{stock}}`.
    case AlertMessageType.ReorderPartial:
      return orders?.reorderPartialMessage
        ? orders.reorderPartialMessage.replace('{{count}}', String(extraData))
        : ''
    case AlertMessageType.ReorderFailed:
      return orders?.reorderErrorMessage || ''
    /*
     * A picker's write failed, not its read.
     *
     * Separate from `paymentMethod.errorMessage` / `deliveryDate.errorMessage`
     * on purpose: those say the options could not be loaded, and a shopper who
     * is looking at the options and tapped one is owed a different sentence.
     * The read failure is a screen-level message; this is a toast over a
     * screen that still works.
     */
    case AlertMessageType.SetPaymentMethod:
      return paymentMethod?.saveErrorMessage || ''
    case AlertMessageType.SetDeliveryDate:
      return deliveryDate?.saveErrorMessage || ''
    /*
     * A rule blocked the order and this storefront authored no screen for it.
     *
     * The one case where cinnamon has nothing to offer the shopper: the
     * engine named a rule, `IConfig.checkoutRuleRoutes` has no entry for it,
     * and there is no picker to send them to. So the copy points at the
     * conversation rather than at anything in the webview — it is the only
     * place the order can still be finished.
     *
     * Read from `errors` rather than from a per-rule namespace on purpose.
     * The shopper is not on a picker screen and may never have seen one, and
     * naming the rule would describe a step this storefront does not offer.
     */
    case AlertMessageType.CheckoutRuleRouteMissing:
      return errors?.checkoutRuleRouteMissing || ''
    default:
      return ''
  }
}

export default getAlertErrorMessage
